import React, { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import Link from '@mui/material/Link';
import GET_MFE_PROFILE_INFO from '../queries/getMFEProfileInfo';
import {
  UPDATE_MFE_PROFILE_WITH_COMPANY,
  UPDATE_MFE_PROFILE_WITHOUT_COMPANY,
} from '../queries/updateMFEProfileInfo';
import { useQuery, useMutation } from '@apollo/client';

import getUserIdFromToken from '../helpers/getUserIdFromToken';

import {
  matchObjectsByID,
  filteredArray,
  getSelectedRole,
  selectedIndustryIds,
  selectedIndustryNames,
  matchArraysByItem,
} from '../helpers';
import { sanitizeObject } from '../helpers/sanitizeObject';
import useAnalytics from '../hooks/useAnalytics';
import ProfileMFEViewSelector from './ProfileMFEViewSelector';
import Fields from './Fields';
import Footer from './Footer';
import ErrorAlert from './Error/ErrorAlert';
import ErrorComponent from './Error/ErrorComponent';
import { sendLog, sendInfoLog } from '../helpers/logs/ProfileLogger';
import AppHeader from './AppHeader';
import WelcomeHeader from './WelcomeHeader';
import WelcomeHeaderSkeleton from './skeletons/WelcomeHeaderSkeleton';
import FooterSkeleton from './skeletons/FooterSkeleton';
import ConditionalRender from './common/ConditionalRender';
import EnvVars from '../constants/env';
import appIdMap from '../constants/appIdMap';
import { getSesLocale } from '../translation/localeMaps';
import { Environment, IndustriesStateType } from '../types/app';

import {
  getCloudEvent,
  sendCloudEvent,
  CloudEventParams,
} from '../helpers/cloudEventsHelper';
import { MFEStates } from '../types/app';
import { useAdpAnalytics } from '@profile-ui-mono/utilities/context/AdpAnalyticsProvider';
import { adpAnalyticsEvents } from '@profile-ui-mono/utilities/constants/adpAnalyticsEvents';
import { useLdFlags } from '@profile-ui-mono/utilities/hooks';
import { FLAG_NAMES } from '@profile-ui-mono/utilities/constants';
import {
  processIndustryValue,
  processRoleValue,
  processCompanyValue,
} from '../helpers/adpAnalyticsHelper';
import { performanceMetrics } from '../helpers/performanceMetrics';

type ErrorObjType = {
  title?: string;
  message?: string;
};

type ProfessionalInfo = {
  company?: string;
  roleId: string;
  jobTitle: string;
  industryTaxonomyIds: any[];
};

type ProfileMFELayoutProps = {
  env?: Environment;
  locale?: string;
  appId?: string;
  description?: string;
  saveText?: string;
  cancelText?: string;
  continueText?: string;
  token: string;
  successMessage?: string;
  successTitle?: string;
  linkText?: string;
  linkUrl?: string;
  errorObj?: ErrorObjType;
  title?: string;
  message?: string;
  isModalOpen?: boolean;
  isCompanyHidden?: boolean;
  browserView?: undefined | 'fullpage';
  cancelBtnStyle?: object;
  successBtnStyle?: object;
  modalProps?: object;
  industryProps?: object;
  roleProps?: object;
  companyProps?: object;
  variant?: string;
  onSave?: (data: string | object) => void;
  onClose?: () => void;
  skeletons?: React.ReactNode;
};

type RoleStateType = {
  roleId: string;
  roleName: string;
};

const ProfileMFELayout: React.FC<ProfileMFELayoutProps> = (
  props: ProfileMFELayoutProps
) => {
  const {
    env,
    locale,
    appId,
    description,
    saveText,
    cancelText,
    continueText,
    successMessage,
    successTitle,
    token,
    linkText,
    linkUrl,
    errorObj: { title, message } = {},
    isModalOpen,
    isCompanyHidden,
    browserView,
    cancelBtnStyle,
    successBtnStyle,
    modalProps,
    industryProps,
    roleProps,
    companyProps,
    variant,
    onSave,
    onClose,
  } = props;
  const { t } = useTranslation();
  const adp = useAdpAnalytics();
  const flags = useLdFlags();

  const { logEvent } = useAnalytics({ appId });
  const [isOpen, setIsOpen] = useState<boolean>(isModalOpen);
  const [image, setImage] = useState<string>('');
  const [company, setCompany] = useState<string>('');
  const [role, setRole] = useState<RoleStateType>({ roleId: '', roleName: '' });
  const [isRoleCompLoaded, setIsRoleCompLoaded] = useState<boolean>(false);

  const [initialIndustryList, setInitialIndustryList] = useState([]);
  const [industries, setIndustries] = useState<IndustriesStateType[]>([]);
  const [isIndustryCompLoaded, setIsIndustryCompLoaded] = useState<boolean>(false);

  const [success, setSuccess] = useState<boolean>(false);
  const [isErrorOnSave, setIsErrorOnSave] = useState<boolean>(false);
  const [areFieldsUpdated, setAreFieldsUpdated] = useState<boolean>(false);
  const [isDataParsed, setIsDataParsed] = useState<boolean>(false);
  const userId = getUserIdFromToken(token);
  const isRuLocaleDisabled = flags[FLAG_NAMES.RU_LOCALE_DISABLED];
  const sesLocale = getSesLocale(locale, isRuLocaleDisabled);

  const { startMark, endMark, measureAndLog, clearMarks, clearMeasures } =
    performanceMetrics({ appId });

  const UPDATE_MFE_PROFILE = isCompanyHidden
    ? UPDATE_MFE_PROFILE_WITHOUT_COMPANY
    : UPDATE_MFE_PROFILE_WITH_COMPANY;

  const { loading, error, data } = useQuery(GET_MFE_PROFILE_INFO, {
    variables: {
      userProfileFilter: { id: userId },
      rolesFilter: { lang: sesLocale },
      subSegmentsFilter: { lang: sesLocale },
    },
    context: {
      headers: {
        authorization: token,
      },
    },
    fetchPolicy: 'network-only',
    onError: (queryError: any) => {
      const errorInfo = `userId: ${userId}, locale: ${locale}, operation: MFEGraphQLQuery`;
      sendLog(queryError, errorInfo, 'ProfileMFELayout');
    },
  });

  const [updateItem, { error: mutationError }] = useMutation(UPDATE_MFE_PROFILE, {
    onError: (updateError: any) => {
      setAreFieldsUpdated(false);
      setIsErrorOnSave(true);
      setSuccess(false);
      const errorInfo = `userId: ${userId}, locale: ${locale}, operation: MFEGraphQLUpdate`;
      sendLog(updateError, errorInfo, 'ProfileMFELayout');
    },
    onCompleted: () => {
      endMark('MFE-save-end');
      measureAndLog('MFE-on-save', 'MFE-save-start', 'MFE-save-end');
      clearMarks(['MFE-save-start', 'MFE-save-end']);
      clearMeasures(['MFE-on-save']);
      setAreFieldsUpdated(false);
      setIsErrorOnSave(false);
      setSuccess(true);
    },
  });

  useEffect(() => {
    if (!data?.userProfile || mutationError) return;

    const allRoles = data?.roles?.roles;
    const allSegments = data?.subSegments?.subSegments;

    const {
      userProfile: { personalInfo },
    } = data;

    // Always log essential metadata
    const logData: any = {
      operation: 'GetUserProfile',
      userId,
      locale
    };

    // Include full raw response if debug feature flag is enabled
    const enableDebugLogs = flags[FLAG_NAMES.MFE_DEBUG_LOGS];
    if (enableDebugLogs) {
      logData.response = sanitizeObject(data?.userProfile);
    }

    sendInfoLog('ProfileMFELayout', logData);

    logEvent('Profile MFE loaded');

    // Send data to ADP Web SDK
    adp?.track(adpAnalyticsEvents.USER_VIEW, {
      id: 'profile_mfe_loaded',
      name: 'profile-mfe',
      type: 'mfe-app',
    });
    //capture render difference and log it
    endMark('MFE-render-end');
    const renderDuration = measureAndLog('MFE-on-render', 'MFE-render-start', 'MFE-render-end');
    //get the total load duration when the page is ready to interact
    const loadDuration = measureAndLog('MFE-on-load', 'MFE-mount-start', 'MFE-render-end');
    const hostedAppLoadDuration = measureAndLog('MFE-hosted-app-load', 'MFE-hosted-app-start', 'MFE-render-end');

    try {
      adp?.track(adpAnalyticsEvents.METRICS_SETTING, {
        id: 'PROFILE-MFE-RENDER-COMPLETE',
        name: 'PROFILE-MFE-RENDER-COMPLETE',
        attributes: { duration: `${renderDuration}` },
      });
      adp?.track(adpAnalyticsEvents.METRICS_SETTING, {
        id: 'PROFILE-MFE-MODULE-LOAD-COMPLETE',
        name: 'PROFILE-MFE-MODULE-LOAD-COMPLETE',
        attributes: { duration: `${loadDuration}` },
      });
      adp?.track(adpAnalyticsEvents.METRICS_SETTING, {
        id: 'PROFILE-MFE-HOSTED-APP-LOAD-COMPLETE',
        name: 'PROFILE-MFE-HOSTED-APP-LOAD-COMPLETE',
        attributes: { duration: `${hostedAppLoadDuration}` },
      });
    } catch (error) {
        logEvent('Something went wrong: Profile MFE couldn`t send performance metrics to ADP Web SDK');
    }

    clearMarks(['MFE-render-start', 'MFE-render-end']);
    clearMeasures(['MFE-on-render', 'MFE-on-load', 'MFE-hosted-app-load']);

    const onloadEvent: CloudEventParams = getCloudEvent({
      type: MFEStates.PROFILE_MFE_LOADED,
    });
    sendCloudEvent(onloadEvent);

    const selectedRoleId =
      personalInfo?.professionalInfo?.additionalProfessionalInfo?.roleId;
    const selectedJobTitle = personalInfo?.professionalInfo?.jobTitle;

    let selectedRole = { roleId: '', roleName: '' };
    if (selectedRoleId && selectedJobTitle) {
      // we need to validate first if roleId exists, if not we will retrieve info from jobTitle.
      // we then map it with the roles query list to get the object of the selected role.
      selectedRole = getSelectedRole(allRoles, selectedRoleId, selectedJobTitle);
    }

    const seletedIndustriesById = selectedIndustryIds(
      personalInfo?.professionalInfo?.additionalProfessionalInfo?.industryTaxonomyIds
    );
    const selectedIndustries = matchObjectsByID(seletedIndustriesById, allSegments);
    // Keep list of industries for analytics (web sdk)
    setInitialIndustryList(selectedIndustries);

    // we need to remove unused properties of industry for saving it inside mutation.
    const filteredSelectedIndustries = filteredArray(selectedIndustries, [
      '__typename',
      'segmentName',
    ]);

    // Use userThumbnails if feature flag is enabled, otherwise use thumbnails
    const userThumbnailsEnabled = flags[FLAG_NAMES.USERTHUMBNAILS_FIELD_ENABLED];
    logEvent(`Feature flag userThumbnailsEnabled set to: ${userThumbnailsEnabled}`);

    const selectedThumbnails = userThumbnailsEnabled
      ? data?.userProfile?.profileImages?.userThumbnails
      : data?.userProfile?.profileImages?.thumbnails;
    
    setImage(`${selectedThumbnails?.sizeX120}`);
    setCompany(personalInfo?.professionalInfo?.company?.companyName);
    setRole(selectedRole);
    setIndustries(filteredSelectedIndustries);
    setIsDataParsed(true);
  }, [data]);

  const handleSave = () => {
    if (areFieldsUpdated) {
      // Send analytics to ADP Web SDK
      adp?.track(adpAnalyticsEvents.CLICK_COMPONENT, {
        id: 'mfe_save-button',
        name: 'Save',
        type: 'Button',
      });
      const initialCompanyValue =
        data?.userProfile?.personalInfo?.professionalInfo?.company?.companyName;
      const initialRoleValue =
        data?.userProfile?.personalInfo?.professionalInfo?.jobTitle;
      let roleId = '', roleName = '';

      if (role) {
        roleId = role.roleId;
        roleName = role.roleName;
      };

      let professionalInfo: ProfessionalInfo = {
        roleId: roleId,
        jobTitle: roleName,
        industryTaxonomyIds: industries,
      };

      if (!isCompanyHidden) {
        if (company !== initialCompanyValue) {
          adp?.track(adpAnalyticsEvents.CLICK_COMPONENT, processCompanyValue(company));
        }
        professionalInfo.company = company;
      }

      if (roleName !== '' && roleName !== initialRoleValue) {
        adp?.track(adpAnalyticsEvents.CLICK_COMPONENT, processRoleValue(role));
      }

      const curIndustryList = selectedIndustryIds(industries);
      const initialValue = selectedIndustryIds(initialIndustryList);
      const isMatchIndustryLists = matchArraysByItem(initialValue, curIndustryList);
      if (!isMatchIndustryLists) {
        adp?.track(
          adpAnalyticsEvents.CLICK_COMPONENT,
          processIndustryValue(industries)
        );
      }

      startMark('MFE-save-start');
      updateItem({
        variables: {
          input: {
            id: userId,
            professionalInfoInput: professionalInfo,
          },
        },
      });

      const cloudEventObj: CloudEventParams = getCloudEvent({
        type: MFEStates.PROFILE_MFE_SAVED,
        data: professionalInfo,
      });
      sendCloudEvent(cloudEventObj);

      if (typeof onSave === 'function') {
        onSave(cloudEventObj);
      }
    }

    logEvent('Profile MFE save button click');
  };

  const handleClose = () => {
    setIsOpen(false);
    logEvent('Profile MFE cancel button click');
    sendCloudEvent(getCloudEvent({ type: MFEStates.PROFILE_MFE_CLOSED }));

    // Send analytics to ADP Web SDK
    adp?.track(adpAnalyticsEvents.CLICK_COMPONENT, {
      id: 'mfe_cancel-button',
      name: 'Cancel',
      type: 'Button',
    });

    if (typeof onClose === 'function') {
      onClose();
    }
  };

  const roleOnloadCallback = () => {
    setIsRoleCompLoaded(true);
  };

  const roleOnErrorCallback = () => {
    throw new Error('Role component failed to load');
  };

  const industryOnloadCallback = () => {
    setIsIndustryCompLoaded(true);
  };

  const industryOnErrorCallback = () => {
    throw new Error('Industry component failed to load');
  };

  const onValueChanged = (value: any, inputName: string) => {
    setAreFieldsUpdated(true);
    setIsErrorOnSave(false);

    switch (inputName) {
      case 'companyValue':
        setCompany(value);
        break;
      case 'roleValue':
        setRole(value);
        break;
      case 'industryValue':
        setIndustries(value);
        break;
    }
  };

  const { firstName, lastName } = data?.userProfile?.personalInfo?.basicInfo || {};
  const productName = appIdMap[appId]?.productName;
  
  const content = {
    headerText: t('HEADER_TEXT'),
    welcomeMessage: t('WELCOME_MESSAGE', { firstName, lastName }),
    description: description || t('DESCRIPTION_TEXT'),
    saveText: saveText || t('SAVE_BUTTON'),
    cancelText: cancelText || t('CANCEL_BUTTON'),
    continueText:
      continueText ||
      (productName
        ? t('CONTINUE_TO_PRODUCT_BUTTON', { productName })
        : t('CONTINUE_BUTTON')),
    errorContinueText: productName
      ? t('CONTINUE_TO_PRODUCT_BUTTON', { productName })
      : t('CANCEL_BUTTON'),
    successMessage: successMessage || (
      <Trans defaults={t('SUCCESS_MESSAGE')}>
        <Link target="_blank" href={EnvVars[env].ADSK_PROFILE_URL} />
      </Trans>
    ),
    successTitle: successTitle || t('SUCCESS_TITLE'),
    linkText: linkText || t('PRIVACY_LINK_TEXT'),
    linkUrl: linkUrl || EnvVars[env].ADSK_PRIVACY_URL,
    industryLabel: t('INDUSTRY_LABEL'),
    roleLabel: t('ROLE_LABEL'),
    companyLabel: t('COMPANY_LABEL'),
    companyError: t('COMPANY_ERROR_UNSUPP_CHARS'),
  };

  // Along with data readiness, check if role and industry are loaded as well.
  const isRenderReady =
    !loading && isDataParsed && isRoleCompLoaded && isIndustryCompLoaded;

  if (error) {
    return (
      <ErrorComponent
        header={<AppHeader>{content.headerText}</AppHeader>}
        errorTitle={title}
        errorMessage={message || error.message}
        isModalOpen={isModalOpen}
        variant={variant}
        browserView={browserView}
        onClose={handleClose}
        buttonText={content.errorContinueText}
        appId={appId}
      />
    );
  }

  return (
    <ProfileMFEViewSelector
      variant={variant}
      isModalOpen={isOpen}
      onClose={handleClose}
      browserView={browserView}
      header={
        <ConditionalRender
          isLoading={!isRenderReady}
          skeletonComponent={<WelcomeHeaderSkeleton />}
          component={<WelcomeHeader message={content.welcomeMessage} imgSrc={image} />}
        />
      }
      footer={
        <ConditionalRender
          isLoading={!isRenderReady}
          skeletonComponent={<FooterSkeleton />}
          component={
            <Footer
              handleSave={handleSave}
              handleCancel={handleClose}
              saveText={content.saveText}
              cancelText={content.cancelText}
              isSuccess={success}
              isReadyToSave={areFieldsUpdated}
              continueText={content.continueText}
              cancelBtnStyle={cancelBtnStyle}
              successBtnStyle={successBtnStyle}
            />
          }
        />
      }
      {...modalProps}
    >
      <>
        {isErrorOnSave && <ErrorAlert message={mutationError?.message} />}
        <Fields
          env={env}
          description={content.description}
          locale={sesLocale}
          isCompanyHidden={isCompanyHidden}
          companyValue={company}
          industryValue={selectedIndustryIds(industries)}
          roleValue={role}
          onValueChange={onValueChanged}
          isSuccess={success}
          successTitle={content.successTitle}
          successMessage={content.successMessage}
          linkText={content.linkText}
          linkUrl={content.linkUrl}
          industryLabel={content.industryLabel}
          roleLabel={content.roleLabel}
          companyLabel={content.companyLabel}
          industryProps={{
            ...industryProps,
            onLoadCallback: industryOnloadCallback,
            onErrorCallback: industryOnErrorCallback,
          }}
          roleProps={{
            ...roleProps,
            onLoadCallback: roleOnloadCallback,
            onErrorCallback: roleOnErrorCallback,
          }}
          companyProps={{
            ...companyProps,
            errorTranslations: { errorUnsuppChars: content.companyError },
          }}
          isLoading={!isRenderReady}
          isDataParsed={!loading && isDataParsed}
        />
      </>
    </ProfileMFEViewSelector>
  );
};

export default ProfileMFELayout;
